import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL } from '../../../lib/businessInfo';

const WA_FAQS = [
  {
    q: 'Can I buy a car in Oregon if I live in Washington?',
    a: "Absolutely — we do it all the time. There's no restriction on where you buy a vehicle. You'll complete the purchase in Oregon, drive home on a 30-day Oregon transit permit, then register the car at your local Washington DOL. Simple.",
  },
  {
    q: 'Will I pay Oregon sales tax as a Washington resident?',
    a: "No — Oregon has no general sales tax, so you pay zero sales tax at the dealership. However, you will owe Washington state sales tax when you register the vehicle at your home DOL. Where you register is where the taxes apply. Nobody escapes their own DMV.",
  },
  {
    q: 'What fees do I actually pay in Oregon at the dealership?',
    a: "Three fees: documentation fee (capped at $150 by Oregon law), title fee (~$116), and a 2-year registration fee ($86–$152 depending on vehicle weight). That's it. No sales tax, no hidden charges. Many Washington dealerships charge $300–$500+ in doc fees alone — Oregon's $150 cap is a real advantage.",
  },
  {
    q: 'What do I pay at the Washington DOL when I register?',
    a: "Washington sales tax on the purchase price (8.2%–10.25% depending on your county), a registration fee (~$150), and license plates (~$20). If you have a trade-in, Washington deducts its value from the taxable amount before calculating sales tax — so a trade-in saves you real money on your WA tax bill.",
  },
  {
    q: 'How does the trade-in deduction work for Washington buyers?',
    a: "Washington state lets you subtract your trade-in value from the vehicle purchase price before calculating sales tax. On a $25,000 vehicle with a $5,000 trade-in, you pay WA sales tax on $20,000 — not the full $25,000. That can save you $400–$500 depending on your county rate.",
  },
  {
    q: 'How do I get the car home after buying in Oregon?',
    a: "We issue you an Oregon 30-day temporary transit permit at the time of purchase. It's a paper plate that goes in your rear window and lets you legally drive the vehicle in any state while you arrange registration at home. You have 30 days to get it registered in Washington.",
  },
  {
    q: 'Can I get financing as a Washington resident buying in Oregon?',
    a: "Yes — financing works the same regardless of which state you live in. We work with multiple lenders who regularly finance cross-state purchases. Your loan is based on your credit profile, income, and the vehicle — not your zip code.",
  },
  {
    q: 'Do you offer delivery to Washington?',
    a: `Yes. We deliver throughout southwest Washington — Long Beach, Ilwaco, Ocean Park, Longview, Kelso, Vancouver, and beyond. If you'd rather not make the drive to Warrenton, we can bring the vehicle to you. Call us at ${PHONE_DISPLAY} to arrange delivery details.`,
  },
  {
    q: 'Is it worth driving to Warrenton from Long Beach or Ilwaco?',
    a: "For most buyers, yes. We're about 10 minutes from the Astoria-Megler Bridge — the closest Oregon dealership to the Long Beach Peninsula. Oregon's $150 doc fee cap vs. $300–$500+ at many WA lots often covers the gas money. Add our no-pressure process and you'll understand why so many WA coast buyers make the trip.",
  },
  {
    q: 'What documents do I need as a Washington buyer?',
    a: "Bring your Washington driver's license, proof of insurance (you'll need to insure the vehicle before driving it home), and your payment method or financing approval. We handle all the paperwork — title, transit permit, bill of sale — and walk you through exactly what to bring to your WA DOL.",
  },
];

export default function WashingtonBuyerFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="washington-buyers" className="py-16 bg-white border-t border-[#E8E2D6]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex items-start gap-5 mb-10">
          <div className="w-14 h-14 flex items-center justify-center bg-[#1A2332] rounded-xl flex-shrink-0">
            <i className="ri-map-pin-2-line text-[#E8B84B] text-2xl"></i>
          </div>
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold uppercase tracking-wide mb-2">
              <i className="ri-flag-line text-xs"></i>
              For Washington Buyers
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A2332] leading-tight">
              Buying in Oregon as a Washington Resident
            </h2>
            <p className="text-[#5A6A7A] mt-2 text-sm leading-relaxed">
              Cross-state purchases are one of our specialties. Here's everything you need to know before you make the drive — or we make the delivery.
            </p>
          </div>
        </div>

        {/* FAQ list */}
        <div className="space-y-2 mb-10">
          {WA_FAQS.map((faq, i) => (
            <div
              key={i}
              className="border border-[#E8E2D6] rounded-xl overflow-hidden hover:border-[#E8B84B] transition-colors"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                aria-expanded={openIndex === i}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-white hover:bg-[#FFFBF0] transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#E8B84B]"
              >
                <span className="font-semibold text-[#1A2332] text-sm sm:text-base pr-2">{faq.q}</span>
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full bg-[#F0EDE6] flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 bg-[#E8B84B]' : ''}`}
                  aria-hidden="true"
                >
                  <i className={`ri-arrow-down-s-line text-xl ${openIndex === i ? 'text-white' : 'text-[#1A2332]'}`}></i>
                </div>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5 pt-1 border-t border-[#E8E2D6] bg-[#FAFAF8]">
                  <p className="text-[#374151] text-sm leading-relaxed pt-3">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA strip */}
        <div className="bg-gradient-to-br from-[#1A2332] to-[#2A3F2E] rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-5">
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-base mb-1">Still have questions about the cross-state process?</p>
            <p className="text-white/60 text-sm">We walk every Washington buyer through the full picture — no surprises at the DOL.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href={`tel:${PHONE_TEL}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#E8B84B] text-[#1A2332] font-bold rounded-xl text-sm hover:bg-[#d4a43d] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line"></i>
              Call Us
            </a>
            <Link
              to="/best-awd-cars-oregon-washington"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white/10 text-white font-semibold rounded-xl text-sm hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20"
            >
              <i className="ri-calculator-line"></i>
              WA Cost Calculator
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}
